// components/patrol-alerts/patrol-alerts.component.ts
import { Component, OnInit, OnDestroy, signal, inject, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { 
  LucideAngularModule, 
  Bell, 
  BellRing, 
  AlertTriangle, 
  X, 
  CheckCircle,
  Circle,
  Clock,
  MapPin,
  AlertCircle
} from 'lucide-angular';
import { PatrolMonitorService, PatrolAlert } from '../../../../core/services/patrol-monitor.service';
import { ToastService } from '../../../../shared/toast-alert/toast.service';

@Component({
  selector: 'app-patrol-alerts',
  standalone: true,
  imports: [CommonModule, RouterModule, LucideAngularModule],
  templateUrl: `./patrol-alerts.component.html`,
  styles: [`
    .line-clamp-2 {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  `]
})
export class PatrolAlertsComponent implements OnInit, OnDestroy {
  // Signals
  isOpen = signal<boolean>(false);
  private patrolService = inject(PatrolMonitorService);
  private openDropdownSubscription!: Subscription;
  
  // Icons
  readonly BellIcon = Bell;
  readonly BellRingIcon = BellRing;
  readonly AlertTriangleIcon = AlertTriangle;
  readonly AlertCircleIcon = AlertCircle;
  readonly XIcon = X;
  readonly CheckCircleIcon = CheckCircle;
  readonly CircleIcon = Circle;
  readonly ClockIcon = Clock;
  readonly MapPinIcon = MapPin;

  // Computed signals do service
  activeAlerts = this.patrolService.activeAlerts;
  alertCount = this.patrolService.alertCount;
  isConnected = this.patrolService.isConnected$;
  hasUnreadAlerts = this.patrolService.hasUnread$;

  constructor(
    private toastService: ToastService,
  ) {
  
    effect(() => {
      const newAlert = this.patrolService.newAlert$();
      if (newAlert) {
        this.showToastPopup(newAlert);
      }
    });
  }

  ngOnInit(): void {
    this.patrolService.connect();
    
    //  ABRIR O DROPDOWN AUTOMATICAMENTE
    this.openDropdownSubscription = this.patrolService.openDropdown$.subscribe({
      next: (shouldOpen) => {
        if (shouldOpen) {
          this.isOpen.set(true);
          this.markAsRead();
          
          //  Fecha automaticamente após 10 segundos
          setTimeout(() => {
            this.isOpen.set(false);
          }, 10000);
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.patrolService.disconnect();
    if (this.openDropdownSubscription) {
      this.openDropdownSubscription.unsubscribe();
    }
  }

  /**
   * Mostra pop-up de notificação
   */
  private showToastPopup(alert: PatrolAlert): void {
    this.toastService.warning(
      alert.description,
      `${alert.title}`,
      {
        duration: 10000,
        position: 'top-right',
        onClick: () => {
          this.isOpen.set(true);
          this.markAsRead();
        },
        action: {
          label: 'Ver alerta',
          onClick: () => {
            this.isOpen.set(true);
            this.markAsRead();
          }
        }
      }
    );
  }

  toggleDropdown(): void {
    this.isOpen.update(v => !v);
    if (this.isOpen()) {
      this.markAsRead();
    }
  }

  markAsRead(): void {
    this.patrolService.markAsRead();
  }

  resolveAlert(alertId: string): void {
    this.patrolService.resolveAlert(alertId);
  }

  handleChangeView(): void {
    localStorage.setItem("dashboardActiveSection", "alerts");
  }

  getAlertClasses(type: string): string {
    const classes: Record<string, string> = {
      'no_movement': 'border-red-300 bg-red-50/50',
      'patrol_delay': 'border-orange-300 bg-orange-50/50',
      'patrol_missing': 'border-red-300 bg-red-50/50',
    };
    return classes[type] || 'border-gray-200 bg-gray-50';
  }

  formatTime(date: Date): string {
    return this.patrolService.formatAlertTime(date);
  }
}